import { AcceptedObjectMessageType, IAcceptedObject } from 'types/apiTypes';
import Modal from './Modal';
import { useAcceptedObjectsQuery } from 'queries/useAcceptedObjectsQuery';
import { useState } from 'react';
import { useFormatDateTimeForTimezone } from 'utils/dateTime';
import Button from './Button';
import { RefreshCwIcon } from 'lucide-react';
import Spinner from './Spinner';
import usePresetStore from 'store/presetStore';
import AcceptedObjectForm from './forms/AcceptedObjectForm';
import Pagination from './Pagination';
import Table from './Table';
import { useMutation } from '@tanstack/react-query';
import { deleteAcceptedObjectsData, postAcceptedObjectsData } from 'utils/api';
import Toast from './Toast';

interface IAcceptedObjectsProps {
  messageType: AcceptedObjectMessageType;
  showModal: boolean;
  onCloseModal: () => void;
}

export default function AcceptedObjects({
  messageType,
  showModal,
  onCloseModal,
}: IAcceptedObjectsProps) {
  const formatDateTimeForTimezone = useFormatDateTimeForTimezone();
  const pageSize = 20;
  const [newModalOpen, setNewModalOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const {
    data: acceptedObjectsData,
    isFetching: isFetchingAcceptedObjects,
    refetch: refetchAcceptedObjectsData,
  } = useAcceptedObjectsQuery(messageType, currentPage, pageSize);

  const totalPages = acceptedObjectsData
    ? Math.ceil(acceptedObjectsData.numberOfObjects / pageSize)
    : 1;

  const { tollChargerValues } = usePresetStore();

  const useAcceptedObjectPostMutation = useMutation({
    mutationFn: (newData: IAcceptedObject) =>
      postAcceptedObjectsData(newData.aduIdentifier, newData.senderActorCode, messageType),
    onSuccess: async () => {
      await refetchAcceptedObjectsData();

      setNewModalOpen(false);

      Toast.success({
        title: 'Success!',
        content: 'Created new accepted object',
      });
    },
    onError: async () => {
      Toast.error({
        title: 'Error!',
        content: 'Failed to create new accepted object',
      });
    },
  });

  const useAcceptedObjectDeleteMutation = useMutation({
    mutationFn: (newData: IAcceptedObject) => deleteAcceptedObjectsData(newData),
    onSuccess: async () => {
      await refetchAcceptedObjectsData();
      setNewModalOpen(false);
      Toast.success({
        title: 'Success!',
        content: 'Deleted accepted object',
      });
    },
    onError: async () => {
      Toast.error({
        title: 'Error!',
        content: 'Failed to delete accepted object',
      });
    },
  });

  return (
    <>
      <Modal wide open={showModal} onClose={() => onCloseModal && onCloseModal()}>
        <div className="w-full lg:min-w-[600px]">
          <h2 className="mb-4 flex items-baseline justify-start text-xl font-semibold">
            Accepted objects {isFetchingAcceptedObjects && <Spinner size="sm" className="ml-2" />}
          </h2>

          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              setPage={setCurrentPage}
            />
          </div>

          <div>
            Showing page {currentPage} of {totalPages} ({acceptedObjectsData?.numberOfObjects}{' '}
            records)
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            <Table className="w-full text-sm">
              <thead className="">
                <Table.Tr>
                  <Table.Th>aduIdentifier</Table.Th>
                  <Table.Th>senderActorCode</Table.Th>
                  <Table.Th>createdAt</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </thead>
              <tbody>
                {acceptedObjectsData?.acceptedObjects.map((obj) => {
                  const actorName =
                    tollChargerValues.find((tc) => tc.value === obj.senderActorCode)?.label ??
                    'Unknown';
                  return (
                    <Table.Tr key={`${obj.aduIdentifier}-${obj.createdAt}`}>
                      <Table.Td>{obj.aduIdentifier}</Table.Td>
                      <Table.Td>
                        {actorName} ({obj.senderActorCode})
                      </Table.Td>
                      <Table.Td className="whitespace-nowrap">
                        {formatDateTimeForTimezone(obj.createdAt)}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={useAcceptedObjectDeleteMutation.isPending}
                          onClick={() => {
                            // Delete row
                            useAcceptedObjectDeleteMutation.mutate(obj);
                          }}
                        >
                          Delete
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              setPage={setCurrentPage}
            />
          </div>

          <div className="mt-4 flex justify-between">
            <Button
              size="sm"
              onClick={() => {
                refetchAcceptedObjectsData();
              }}
              variant="secondary"
              disabled={isFetchingAcceptedObjects}
            >
              Refetch data
              <RefreshCwIcon className="ml-2" size={18} />
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setNewModalOpen(true);
              }}
              variant="primary"
            >
              New accepted object
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        wide
        open={newModalOpen}
        onClose={() => {
          if (!useAcceptedObjectPostMutation.isPending) {
            setNewModalOpen(false);
          }
        }}
      >
        <div className="w-full lg:min-w-[400px]">
          <h2 className="mb-4 flex items-baseline justify-start text-xl font-semibold">
            New accepted object
          </h2>

          <div className="max-h-[50vh]">
            <AcceptedObjectForm
              loading={useAcceptedObjectPostMutation.isPending}
              onSubmit={(newData) => {
                if (!useAcceptedObjectPostMutation.isPending) {
                  useAcceptedObjectPostMutation.mutate(newData);
                }
              }}
              onCancel={() => {
                if (!useAcceptedObjectPostMutation.isPending) {
                  setNewModalOpen(false);
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
